/**
 * @file One table row as a card: the title column's value in bold, then every
 * other column as a `ResponsiveTableCardLine`, then the row's expand control and its
 * expanded content. Consumer: `ResponsiveTableCards`, which owns selection,
 * expansion and sorting and hands the card its controls ready-made. A cell reads
 * exactly what antd's cell would: the column's `render(value, record, index)`,
 * else the raw `dataIndex` value, where an empty path means the whole record.
 * The row's `onRow` attributes land on the card, so its click, key and label
 * handlers keep working.
 */
import { isValidElement, type ReactNode } from 'react';
import { Typography } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import type { ColumnType } from 'antd/es/table/interface';

import FlexBox from '../FlexBox';
import FlexCard from '../FlexCard';
import FlexCol from '../FlexCol';
import ResponsiveTableCardLine from './ResponsiveTableCardLine';
import type { ResponsiveTableCardProps, ResponsiveTableLeaf } from './ResponsiveTable.types';

const { Text } = Typography;

type Rendered<T extends AnyObject> = ReturnType<NonNullable<ColumnType<T>['render']>>;

const isRenderedCell = <T extends AnyObject>(
  node: Rendered<T>,
): node is Exclude<Rendered<T>, ReactNode> =>
  typeof node === 'object' &&
  node !== null &&
  !isValidElement(node) &&
  !(Symbol.iterator in node) &&
  !('then' in node);

const cellOf = <T extends AnyObject>(
  { column, path }: ResponsiveTableLeaf<T>,
  record: T,
  index: number,
): ReactNode => {
  const value: unknown = path.reduce<unknown>(
    (current, segment) =>
      typeof current === 'object' && current !== null ? Reflect.get(current, segment) : undefined,
    record,
  );
  if (!column.render) {
    return isValidElement(value) || typeof value === 'string' || typeof value === 'number'
      ? value
      : null;
  }
  const rendered = column.render(value, record, index);
  return isRenderedCell(rendered) ? rendered.children : rendered;
};

const ResponsiveTableCard = <T extends AnyObject>({
  record,
  index,
  titleLeaf,
  bodyLeaves,
  rowAttributes,
  selectControl,
  expandControl,
  expandedContent,
}: ResponsiveTableCardProps<T>): ReactNode => {
  const { style, className, title: _title, ...attributes } = rowAttributes;
  const clickable = Boolean(attributes.onClick);

  return (
    <FlexCard
      role="listitem"
      hoverable={clickable}
      className={className}
      {...attributes}
      style={{ width: '100%', cursor: clickable ? 'pointer' : undefined, ...style }}
    >
      <FlexBox justifyStart alignStart>
        {selectControl}
        {titleLeaf && <Text strong>{cellOf(titleLeaf, record, index)}</Text>}
      </FlexBox>
      {bodyLeaves.map((leaf) => {
        const label = typeof leaf.column.title === 'function' ? leaf.column.title({}) : leaf.column.title;
        return <ResponsiveTableCardLine key={leaf.key} label={label} value={cellOf(leaf, record, index)} />;
      })}
      {expandControl}
      {expandedContent !== undefined && (
        <FlexCol stretch style={{ width: '100%' }}>
          {expandedContent}
        </FlexCol>
      )}
    </FlexCard>
  );
};

export default ResponsiveTableCard;
